/**
 * notificationService.js
 * Phase 13 - Smart Notifications
 *
 * API functions for the Notifications feature.
 * Uses the same auth pattern as documentService.js (fetchAuthSession idToken).
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import appConfig from '../config/config';

/**
 * Resolve the Notifications API base URL.
 * Falls back to the chat endpoint base + '/notifications' when
 * REACT_APP_NOTIFICATIONS_ENDPOINT is not configured.
 *
 * @returns {string}
 */
function getBaseUrl() {
  const explicit = appConfig.config?.api?.notificationsEndpoint || appConfig.api?.notificationsEndpoint;
  if (explicit) return explicit;

  // Fall back: strip trailing path segment from chatEndpoint and append /notifications
  const chatEndpoint =
    appConfig.config?.api?.chatEndpoint ||
    appConfig.api?.chatEndpoint ||
    'https://x14bncspba.execute-api.ap-southeast-2.amazonaws.com/prod';

  // Remove trailing /chat or /prod/chat etc. if present, then append /notifications
  const base = chatEndpoint.replace(/\/chat\/?$/, '');
  return `${base}/notifications`;
}

/**
 * Retrieve an Authorization header value using Cognito idToken.
 * @returns {Promise<string>}
 */
async function getAuthHeader() {
  const session = await fetchAuthSession();
  const idToken = session?.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Not authenticated - no idToken available.');
  return `Bearer ${idToken}`;
}

// ─── Public API functions ─────────────────────────────────────────────────────

/**
 * List all notifications for the authenticated user.
 *
 * @returns {Promise<{ notifications: Array, unreadCount: number, total: number }>}
 */
export async function listNotifications() {
  const base = getBaseUrl();
  const auth = await getAuthHeader();

  const response = await fetch(base, {
    method: 'GET',
    headers: {
      Authorization: auth,
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to list notifications (${response.status}): ${body}`);
  }

  return response.json();
}

/**
 * Mark a notification as read.
 *
 * @param {string} notificationId
 * @returns {Promise<{ success: boolean }>}
 */
export async function markRead(notificationId) {
  const base = getBaseUrl();
  const auth = await getAuthHeader();

  const response = await fetch(`${base}/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST',
    headers: {
      Authorization: auth,
      'Content-Type': 'application/json',
    },
    body: '{}',
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to mark notification read (${response.status}): ${body}`);
  }

  return response.json();
}

/**
 * Soft-dismiss a notification (removes it from the inbox).
 *
 * @param {string} notificationId
 * @returns {Promise<{ success: boolean }>}
 */
export async function dismissNotification(notificationId) {
  const base = getBaseUrl();
  const auth = await getAuthHeader();

  const response = await fetch(`${base}/${encodeURIComponent(notificationId)}`, {
    method: 'DELETE',
    headers: {
      Authorization: auth,
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to dismiss notification (${response.status}): ${body}`);
  }

  return response.json();
}

/**
 * Schedule EventBridge appeal deadline reminder notifications.
 *
 * Identity note: the API derives the user from the Cognito authorizer
 * (requestContext.authorizer.claims.sub) - a client-supplied userId is never
 * trusted, so it is deliberately not sent.
 *
 * @param {{ appealId: string, deadlineDate: string, pathway: string }} params
 * @returns {Promise<{ scheduled: string[] }>}
 */
export async function scheduleAppealReminders({ appealId, deadlineDate, pathway }) {
  const base = getBaseUrl();
  const auth = await getAuthHeader();

  const response = await fetch(`${base}/schedule-appeal`, {
    method: 'POST',
    headers: {
      Authorization: auth,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ appealId, deadlineDate, pathway }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to schedule appeal reminders (${response.status}): ${body}`);
  }

  return response.json();
}
